import { SxProps, Theme } from '@mui/material/styles';

import { createHoverBackground } from '../utils/ThemeUtils';

// Onboard component styles
export const onboardStyles = {
  mainContainer: {
    // Mobile: Remove container, content fills screen
    display: { xs: 'block', sm: 'flex' },
    flexDirection: { xs: 'column', sm: 'column' },
    alignItems: { xs: 'stretch', sm: 'center' },
    justifyContent: { xs: 'flex-start', sm: 'center' },
    minHeight: '100vh',
    py: { xs: 0, sm: 0 },
    px: { xs: 0, sm: 2 },
    pt: { xs: 0, sm: 0 },
  },

  paper: {
    // Mobile: Full screen, no card appearance
    width: { xs: '100%', sm: '545px' },
    minWidth: { xs: '100vw', sm: '545px' },
    maxWidth: { xs: '100vw', sm: 'calc(100% - 32px)' },
    minHeight: { xs: '100vh', sm: 'auto' }, // Full height on mobile
    borderRadius: { xs: '0', sm: '4px' },
    overflow: 'hidden',
    mb: { xs: 0, sm: 2 },
    mx: { xs: 0, sm: 'auto' },
    px: 0,
    py: 0,
    position: 'relative',
    // Remove elevation/shadow on mobile for true full-screen feel
    boxShadow: {
      xs: 'none',
      sm: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
    },
  },

  headerContainer: {
    padding: { xs: '24px 16px 0px', sm: '32px 50px 0px' },
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
    minWidth: { xs: 'calc(100vw - 48px)', sm: 'auto' },
  },

  backButton: (theme: Theme) => ({
    position: 'absolute',
    left: '0px',
    top: '0px',
    minWidth: { xs: '48px', sm: '60px' },
    width: { xs: '48px', sm: '60px' },
    height: { xs: '48px', sm: '60px' },
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: theme.palette?.action?.hover || 'rgba(0, 0, 0, 0.04)',
    },
  }),

  backIcon: (theme: Theme) => ({
    color: theme.palette?.text?.secondary || '#5C656A',
    width: { xs: '20px', sm: '24px' },
    height: { xs: '20px', sm: '24px' },
  }),

  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    mt: { xs: 3, sm: 4 },
    px: 0,
    width: '100%',
    maxWidth: '100%',
    mx: 'auto',
    pb: 0,
  },

  avatar: {
    width: { xs: 80, sm: 100 },
    height: { xs: 80, sm: 100 },
    mb: 2,
    objectFit: 'contain',
  },

  titleContainer: {
    textAlign: 'center',
    px: { xs: 1, sm: 2 },
    mb: 3,
    width: '100%',
    maxWidth: '445px',
    mx: 'auto',
  },

  title: (theme: Theme) => ({
    mb: 1,
    fontWeight: 600,
    fontSize: { xs: '20px', sm: '24px' },
    lineHeight: { xs: '26px', sm: '32px' },
    fontFamily: theme.typography?.fontFamily || 'Inter, sans-serif',
    width: '100%',
    color: theme.palette?.text?.primary,
    wordWrap: 'break-word',
    hyphens: 'auto',
  }),

  subtitle: (theme: Theme) => ({
    mb: 2,
    fontSize: { xs: '13px', sm: '14px' },
    lineHeight: { xs: '18px', sm: '20px' },
    letterSpacing: '0.15px',
    fontFamily: theme.typography?.fontFamily || 'Inter, sans-serif',
    width: '100%',
    fontWeight: 400,
    color: theme.palette?.text?.secondary,
  }),

  formContainer: {
    width: '100%',
    maxWidth: '445px',
    minWidth: { xs: 'calc(100vw - 48px)', sm: 'auto' },
    mx: 'auto',
    padding: { xs: '0 16px 0px', sm: '0 50px 0px' },
    pt: 0,
  },

  loginFormContainer: {
    width: '100%',
    mb: 3,
  },

  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    mt: 0,
    mb: 4,
  },

  submitButton: (isFormValid: boolean, theme: Theme): SxProps<Theme> => {
    const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
    const disabledBg = theme.palette?.action?.disabledBackground || '#E5E6E7';
    const disabledText = theme.palette?.action?.disabled || '#C7CACB';
    const contrastText = theme.palette?.primary?.contrastText || '#FFFFFF';

    return {
      backgroundColor: isFormValid ? primaryColor : disabledBg,
      color: isFormValid ? contrastText : disabledText,
      height: { xs: '48px', sm: '40px' },
      width: '100%',
      borderRadius: '4px',
      textTransform: 'uppercase',
      fontWeight: 700,
      fontSize: { xs: '16px', sm: '14px' },
      lineHeight: '16px',
      letterSpacing: '0.75px',
      padding: '12px',
      fontFamily: theme.typography?.fontFamily || 'Inter, sans-serif',
      '&.Mui-disabled': {
        backgroundColor: disabledBg,
        color: disabledText,
      },
    };
  },

  helpModalStyle: (theme: Theme) => ({
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: 'calc(100% - 32px)', sm: 400 },
    maxWidth: { xs: '90vw', sm: 400 },
    bgcolor: 'background.paper',
    border: `1px solid ${theme.palette?.divider || '#D3D3D3'}`,
    boxShadow: 24,
    p: { xs: 2, sm: 4 },
    borderRadius: 1,
  }),

  footerContainer: {
    bgcolor: 'background.paper',
    padding: { xs: '16px 16px 24px', sm: '16px 50px 24px' },
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },

  helpButton: (theme: Theme): SxProps<Theme> => ({
    color: theme.palette?.primary?.main || '#B60207',
    textTransform: 'none',
    fontSize: { xs: '13px', sm: '14px' },
    fontWeight: 400,
    textDecoration: 'underline',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
    padding: '8px 4px',
    minHeight: 'auto',
  }),
};

// Error page styles
export const errorPageStyles = {
  mainContainer: {
    // Mobile: Remove container, content fills screen
    display: { xs: 'block', sm: 'flex' },
    flexDirection: { xs: 'column', sm: 'column' },
    alignItems: { xs: 'stretch', sm: 'center' },
    justifyContent: { xs: 'flex-start', sm: 'center' },
    minHeight: '100vh',
    py: { xs: 0, sm: 0 },
    px: { xs: 0, sm: 2 },
    pt: { xs: 0, sm: 0 },
    position: 'relative',
  },

  paper: {
    // Mobile: Full screen, no card appearance
    width: { xs: '100%', sm: '520px' },
    minWidth: { xs: '100vw', sm: '520px' },
    maxWidth: { xs: '100vw', sm: 'calc(100% - 32px)' },
    minHeight: { xs: '100vh', sm: 'auto' }, // Full height on mobile
    borderRadius: { xs: '0', sm: '8px' },
    position: 'relative',
    zIndex: 1,
    mx: { xs: 0, sm: 'auto' },
    mb: { xs: 0, sm: 2 },
    p: { xs: '32px 16px 40px', sm: '32px 50px 40px' },
    // Remove elevation/shadow on mobile for true full-screen feel
    boxShadow: {
      xs: 'none',
      sm: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
    },
  },

  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    pt: 3,
    pb: 2,
  },

  title: (theme: Theme) => ({
    mb: 1,
    fontFamily: theme.typography?.fontFamily || 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: { xs: '22px', sm: '28px' },
    lineHeight: { xs: '28px', sm: '36px' },
    textAlign: 'center',
    color: theme.palette?.text?.primary,
    wordWrap: 'break-word',
    hyphens: 'auto',
  }),

  caption: (theme: Theme) => ({
    mb: 3,
    fontFamily: theme.typography?.fontFamily || 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: { xs: '14px', sm: '16px' },
    lineHeight: { xs: '20px', sm: '24px' },
    letterSpacing: '0.15px',
    textAlign: 'center',
    color: theme.palette?.text?.primary,
  }),

  buttonsContainer: {
    pt: 3,
  },

  primaryButton: (theme: Theme): SxProps<Theme> => {
    const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
    const contrastText = theme.palette?.primary?.contrastText || '#FFFFFF';

    return {
      height: { xs: '48px', sm: '40px' },
      width: '100%',
      bgcolor: primaryColor,
      color: contrastText,
      borderRadius: '4px',
      textTransform: 'uppercase',
      fontFamily: theme.typography?.fontFamily || 'Inter, sans-serif',
      fontWeight: 700,
      fontSize: { xs: '16px', sm: '14px' },
      lineHeight: '16px',
      letterSpacing: '0.75px',
      boxShadow:
        '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 8px rgba(0, 0, 0, 0.1)',
      '&:hover': {
        bgcolor: primaryColor,
        opacity: 0.9,
      },
    };
  },

  secondaryButton: (theme: Theme): SxProps<Theme> => {
    const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';

    return {
      height: { xs: '48px', sm: '40px' },
      width: '100%',
      color: primaryColor,
      borderRadius: '4px',
      textTransform: 'uppercase',
      fontFamily: theme.typography?.fontFamily || 'Inter, sans-serif',
      fontWeight: 700,
      fontSize: { xs: '16px', sm: '14px' },
      lineHeight: '16px',
      letterSpacing: '0.75px',
      '&:hover': {
        backgroundColor: createHoverBackground(primaryColor, 0.04),
      },
    };
  },
};

// Success page styles (same structure as error page)
export const successPageStyles = {
  mainContainer: {
    // Mobile: Remove container, content fills screen
    display: { xs: 'block', sm: 'flex' },
    flexDirection: { xs: 'column', sm: 'column' },
    alignItems: { xs: 'stretch', sm: 'center' },
    justifyContent: { xs: 'flex-start', sm: 'center' },
    minHeight: '100vh',
    py: { xs: 0, sm: 0 },
    px: { xs: 0, sm: 2 },
    pt: { xs: 0, sm: 0 },
    position: 'relative',
  },

  paper: {
    // Mobile: Full screen, no card appearance
    width: { xs: '100%', sm: '520px' },
    minWidth: { xs: '100vw', sm: '520px' },
    maxWidth: { xs: '100vw', sm: 'calc(100% - 32px)' },
    minHeight: { xs: '100vh', sm: 'auto' }, // Full height on mobile
    borderRadius: { xs: '0', sm: '8px' },
    position: 'relative',
    zIndex: 1,
    mx: { xs: 0, sm: 'auto' },
    mb: { xs: 0, sm: 2 },
    p: { xs: '32px 16px 40px', sm: '32px 50px 40px' },
    // Remove elevation/shadow on mobile for true full-screen feel
    boxShadow: {
      xs: 'none',
      sm: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
    },
  },

  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    pt: 3,
    pb: 2,
  },

  title: (theme: Theme) => ({
    mb: 1,
    fontFamily: theme.typography?.fontFamily || 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: { xs: '22px', sm: '28px' },
    lineHeight: { xs: '28px', sm: '36px' },
    textAlign: 'center',
    color: theme.palette?.text?.primary,
    wordWrap: 'break-word',
    hyphens: 'auto',
  }),

  caption: (theme: Theme) => ({
    mb: 3,
    fontFamily: theme.typography?.fontFamily || 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: { xs: '14px', sm: '16px' },
    lineHeight: { xs: '20px', sm: '24px' },
    letterSpacing: '0.15px',
    textAlign: 'center',
    color: theme.palette?.text?.primary,
  }),

  buttonsContainer: {
    pt: 3,
  },

  primaryButton: (theme: Theme): SxProps<Theme> => {
    const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
    const contrastText = theme.palette?.primary?.contrastText || '#FFFFFF';

    return {
      height: { xs: '48px', sm: '40px' },
      width: '100%',
      bgcolor: primaryColor,
      color: contrastText,
      borderRadius: '4px',
      textTransform: 'uppercase',
      fontFamily: theme.typography?.fontFamily || 'Inter, sans-serif',
      fontWeight: 700,
      fontSize: { xs: '16px', sm: '14px' },
      lineHeight: '16px',
      letterSpacing: '0.75px',
      boxShadow:
        '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 8px rgba(0, 0, 0, 0.1)',
      '&:hover': {
        bgcolor: primaryColor,
        opacity: 0.9,
      },
    };
  },

  secondaryButton: (theme: Theme): SxProps<Theme> => {
    const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';

    return {
      height: { xs: '48px', sm: '40px' },
      width: '100%',
      color: primaryColor,
      borderRadius: '4px',
      textTransform: 'uppercase',
      fontFamily: theme.typography?.fontFamily || 'Inter, sans-serif',
      fontWeight: 700,
      fontSize: { xs: '16px', sm: '14px' },
      lineHeight: '16px',
      letterSpacing: '0.75px',
      '&:hover': {
        backgroundColor: createHoverBackground(primaryColor, 0.04),
      },
    };
  },
};

// Login components styles - minimal styles to replace inline styles
export const loginComponentStyles = {
  container: {
    width: '100%',
    mt: 1,
    mb: 1,
  },
  explanatoryText: {
    mb: 2,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.15px',
    color: 'text.secondary',
  },
  checkboxContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    mt: 1,
  },
  checkbox: {
    pointerEvents: 'auto',
    color: 'primary.main',
  },
  formControlLabel: {
    pointerEvents: 'auto',
    '& .MuiFormControlLabel-label': {
      fontSize: '14px',
      lineHeight: '20px',
    },
  },
};

// Add more component styles below
// ------------------------------

// export const approveStyles = { ... };

// export const selectTSPStyles = { ... };

// etc.
